import * as React from 'react';
import { Hit } from '@algolia/client-search';

import { ImageWithFallback } from '@blockworks/ui';
import { useInfiniteScroll } from '@blockworks/ui/hooks';
import { RenderSimpleHtml } from '@blockworks/ui/legacy';

import { Link } from '@/components/common';
import SectionHeader from '@/components/display/headers/section-header';
import NewsSectionArticle from '@/components/pages/frontpage/components/news/news-section-article';
import { useAlgolia } from '@/context/algolia';
import { AlgoliaPostHit } from '@/types/algolia/algolia-post-hit';
import { ArticleType } from '@/types/article-type';
import mapAlgoliaHits from '@/utils/functions/algolia/map-algolia-hits';
import queryAlgoliaPosts from '@/utils/functions/algolia/query-algolia-posts';
import { AuthorEndpointResult } from '@/utils/functions/wordpress/fetch-author';

type AuthorPageLayoutProps = {
    author: AuthorEndpointResult;
    articles: ArticleType[];
};

const AuthorPageLayout = (props: AuthorPageLayoutProps) => {
    const { author, articles } = props;
    const { algoliaKey, error, loading } = useAlgolia();

    const { renderScrollTarget, entities } = useInfiniteScroll(
        articles,
        async (limit, page) => {
            if (!algoliaKey) {
                return [];
            }

            const posts = await queryAlgoliaPosts(
                '',
                limit,
                page,
                {
                    filters: `post_author.slug:${author.slug}`,
                    proxy: true,
                },
                algoliaKey,
            );
            return mapAlgoliaHits(posts.hits as Array<Hit<AlgoliaPostHit>>);
        },
        10,
    );

    return (
        <main>
            <div className="flex flex-col md:flex-row justify-start items-center lg:items-start self-stretch flex-grow-0 flex-shrink-0">
                <div className="flex flex-col justify-start items-start flex-grow-0 flex-shrink-0 bg-[color:#f8b4e4]">
                    <ImageWithFallback
                        alt={author.display_name}
                        src={author.image_url}
                        width={475}
                        height={475}
                        className="object-cover md:max-h-[600px]"
                    />
                </div>
                <div className="flex flex-col justify-start items-start self-stretch flex-grow">
                    <div className="flex flex-col justify-end items-start self-stretch flex-grow relative gap-1 p-10 bg-[color:#000a39]">
                        <p className="self-stretch flex-grow-0 flex-shrink-0 w-full text-5xl text-left text-white">
                            {author.display_name}
                        </p>
                        {/*
                    <p className="self-stretch flex-grow-0 flex-shrink-0 w-[885px] text-base text-left text-[#e0e3eb]">
                        Reporter
                    </p>
                    */}
                        <div className="flex justify-start items-start flex-grow-0 flex-shrink-0 relative gap-3">
                            {author.twitter && (
                                <Link href={author.twitter} target="_blank">
                                    <svg
                                        width={20}
                                        height={20}
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="flex-grow-0 flex-shrink-0 w-5 h-5 relative"
                                        preserveAspectRatio="xMidYMid meet"
                                    >
                                        <path
                                            d="M16.6666 6.25C16.1666 6.5 15.6666 6.58333 15.0833 6.66667C15.6666 6.33333 16.0833 5.83333 16.25 5.16667C15.75 5.5 15.1666 5.66667 14.5 5.83333C14 5.33333 13.25 5 12.5 5C11.0833 5 9.83331 6.25 9.83331 7.75C9.83331 8 9.83331 8.16667 9.91665 8.33333C7.66665 8.25 5.58331 7.16667 4.24998 5.5C3.99998 5.91667 3.91665 6.33333 3.91665 6.91667C3.91665 7.83333 4.41665 8.66667 5.16665 9.16667C4.74998 9.16667 4.33331 9 3.91665 8.83333C3.91665 10.1667 4.83331 11.25 6.08331 11.5C5.83331 11.5833 5.58331 11.5833 5.33331 11.5833C5.16665 11.5833 4.99998 11.5833 4.83331 11.5C5.16665 12.5833 6.16665 13.4167 7.41665 13.4167C6.49998 14.1667 5.33331 14.5833 3.99998 14.5833C3.74998 14.5833 3.58331 14.5833 3.33331 14.5833C4.58331 15.3333 5.99998 15.8333 7.49998 15.8333C12.5 15.8333 15.25 11.6667 15.25 8.08333C15.25 8 15.25 7.83333 15.25 7.75C15.8333 7.33333 16.3333 6.83333 16.6666 6.25Z"
                                            fill="#9885E0"
                                        />
                                    </svg>
                                </Link>
                            )}
                            {author.linkedin && (
                                <Link href={author.linkedin} target="_blank">
                                    <svg
                                        width={20}
                                        height={20}
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="flex-grow-0 flex-shrink-0 w-5 h-5 relative"
                                        preserveAspectRatio="xMidYMid meet"
                                    >
                                        <path
                                            d="M16.0833 3.33325H3.91665C3.58331 3.33325 3.33331 3.58325 3.33331 3.91659V16.1666C3.33331 16.4166 3.58331 16.6666 3.91665 16.6666H16.1666C16.5 16.6666 16.75 16.4166 16.75 16.0833V3.91659C16.6666 3.58325 16.4166 3.33325 16.0833 3.33325ZM7.24998 14.6666H5.33331V8.33325H7.33331V14.6666H7.24998ZM6.33331 7.49992C5.66665 7.49992 5.16665 6.91659 5.16665 6.33325C5.16665 5.66659 5.66665 5.16659 6.33331 5.16659C6.99998 5.16659 7.49998 5.66659 7.49998 6.33325C7.41665 6.91659 6.91665 7.49992 6.33331 7.49992ZM14.6666 14.6666H12.6666V11.5833C12.6666 10.8333 12.6666 9.91658 11.6666 9.91658C10.6666 9.91658 10.5 10.7499 10.5 11.5833V14.7499H8.49998V8.33325H10.4166V9.16658C10.6666 8.66658 11.3333 8.16659 12.25 8.16659C14.25 8.16659 14.5833 9.49992 14.5833 11.1666V14.6666H14.6666Z"
                                            fill="#9885E0"
                                        />
                                    </svg>
                                </Link>
                            )}
                        </div>
                    </div>
                    <div className="hidden lg:flex flex-col justify-end items-start self-stretch flex-grow-0 flex-shrink-0 relative gap-3 p-10 bg-[color:#f7f7f7]">
                        <RenderSimpleHtml
                            className="self-stretch flex-grow-0 flex-shrink-0 w-full text-lg text-left"
                            html={author.description}
                        />
                    </div>
                </div>
            </div>
            <div className="lg:hidden flex flex-col justify-end items-start self-stretch flex-grow-0 flex-shrink-0 relative gap-3 p-10 bg-[color:#f7f7f7]">
                <RenderSimpleHtml
                    className="self-stretch flex-grow-0 flex-shrink-0 w-full text-lg text-left"
                    html={author.description}
                />
            </div>
            {!error && !loading && entities.length > 0 && (
                <div>
                    <SectionHeader
                        title={`recent news by ${author.display_name}`}
                        linkTitle="All News"
                        linkUrl="/news"
                        className="border-b border-t"
                    />
                    <div className="p-4 grid gap-8 grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                        {entities.map(article => (
                            <NewsSectionArticle key={`NewsSectionArticle-${article.slug}`} article={article} />
                        ))}
                    </div>
                    {renderScrollTarget()}
                </div>
            )}
        </main>
    );
};

export default AuthorPageLayout;
